// Count provider abstraction. Counting/filtering unit = a ZONE: a postcode, or a NIS9 sector
// when the selection is made of real sectors only (see countUnits() in main.js).
//
// DEFAULT provider = 'gis': GIS `POST /dividuals/addresses/count`, looped ONCE PER
// postcode (cached, debounced upstream), Bearer-auth injected by the Function. GIS is
// NOT IP-gated, so this runs live from Cloudflare with no proxy/whitelist.
//   -> { total_addresses, target_addresses, target_percentage }
// Households in selection = Σ total_addresses; after segmentation = Σ target_addresses
// of postcodes whose target_percentage ≥ slider%.
//
// SEAM: provider 'instadata' (The Wave) — kept behind the seam, needs the static-IP
// proxy; also yields real EMAIL counts. Not the default.
//
// EMAIL counts: GIS does NOT return them. We MOCK them deterministically in BOTH
// DEMO_MOCK and live-GIS modes (build brief §5.4). TODO: real EM from Instadata.

import { gisDividualsCount, waveCountSegments } from './api.js';

function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < String(str).length; i++) {
    h ^= String(str).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Deterministic mock email counts derived from household counts (per postcode).
function mockEmail(postcode, totalHH, targetHH) {
  const f = 0.70 + (hash(`em:${postcode}`) % 20) / 100;   // 0.70 .. 0.90
  const tf = 0.50 + (hash(`emt:${postcode}`) % 25) / 100;  // 0.50 .. 0.75
  const pf = 0.55 + (hash(`ph:${postcode}`) % 20) / 100;   // 0.55 .. 0.75 phone
  const ptf = 0.40 + (hash(`pht:${postcode}`) % 25) / 100; // 0.40 .. 0.65
  return {
    emailTotal: Math.round(totalHH * f), emailTarget: Math.round(targetHH * tf),
    phoneTotal: Math.round(totalHH * pf), phoneTarget: Math.round(targetHH * ptf)
  };
}

// Cache keyed by provider + zone + dividuals signature, so toggling a Dividual on and off, or
// re-adding a zone, doesn't refetch what this session already knows.
const cache = new Map();
function sigOf(subsegs) { return subsegs.slice().sort().join(','); }

// A count unit: { id, zoneType: 'zip'|'sector', zone, postcode, totalHH }.
//   postcode mode: id = zone = postcode = the pc4.
//   sector mode:   id = NIS9 (the state key), zone = padded Statbel code for The Wave,
//                  postcode = the sector's pc4 (GIS fallback), totalHH = its GIS address count.

// ---- GIS provider: knows postcodes only ----
async function gisPostcode(pc, subsegs, sig) {
  const key = `gis|${pc}|${sig}`;
  let r = cache.get(key);
  if (!r) {
    const resp = await gisDividualsCount({ postcodes: [pc], segments: [], subsegments: subsegs });
    const total = Number(resp.total_addresses || 0);
    const target = Number(resp.target_addresses || 0);
    const pct = resp.target_percentage != null
      ? Number(resp.target_percentage)
      : (total ? (target / total) * 100 : 0);
    r = { total, target, pct, ...mockEmail(pc, total, target) };
    cache.set(key, r);
  }
  return r;
}

// Target share per statistical sector: /dividuals/addresses/count with details:true returns a
// breakdown per NIS9 code (9 chars, '-' padded) next to the total. Cached per sector + Dividuals.
export async function gisSectors(codes, subsegs) {
  const sig = sigOf(subsegs);
  const out = new Map(), todo = [];
  codes.forEach((c) => { const hit = cache.get(`gis9|${c}|${sig}`); if (hit) out.set(c, hit); else todo.push(c); });
  const chunks = [];
  for (let i = 0; i < todo.length; i += 500) chunks.push(todo.slice(i, i + 500));
  await Promise.all(chunks.map(async (chunk) => {
    const resp = await gisDividualsCount({ postcodes: chunk.map(c => c.padEnd(9, '-')), segments: [], subsegments: subsegs, details: true });
    const det = resp.details || {};
    chunk.forEach((c) => {
      const x = det[c.padEnd(9, '-')] || det[c];
      const r = { total: Number(x?.total_addresses || 0), target: Number(x?.target_addresses || 0), pct: Number(x?.target_percentage || 0) };
      cache.set(`gis9|${c}|${sig}`, r); out.set(c, r);
    });
  }));
  return out;
}

async function gisProvider(units, subsegs, onProgress) {
  const sig = sigOf(subsegs);
  const postcodes = [...new Set(units.map(u => u.postcode).filter(Boolean))];
  const perPc = new Map();
  let done = 0;
  await Promise.all(postcodes.map(async (pc) => {
    perPc.set(pc, await gisPostcode(pc, subsegs, sig));
    done++;
    if (onProgress) onProgress(done, postcodes.length);
  }));
  const results = new Map();
  units.forEach(u => {
    const p = u.postcode ? perPc.get(u.postcode) : null;
    if (!p) return;
    if (u.zoneType === 'zip') { results.set(u.id, p); return; }
    // Sector unit: GIS only counts per postcode, so apply the postcode's penetration to the
    // sector's own address count. An estimate — lastCountSource() says so to the UI.
    const total = u.totalHH || 0;
    const target = Math.round(total * p.pct / 100);
    results.set(u.id, { total, target, pct: p.pct, ...mockEmail(u.id, total, target) });
  });
  return results;
}

// ---- The Wave / Instadata provider ----
// One repeatable block per unit, zoneType 'zip' (postcode) or 'sector' (NIS9). A raw call and a
// segmented call give real dm / em / sms before and after segmentation; the % itself is then
// replaced by the GIS address share (see below).
async function instadataProvider(units, subsegs, onProgress, { lang = null } = {}) {
  // Language is part of the segmented call only: 'in selection' stays everyone in the zone.
  const sig = sigOf(subsegs) + (lang ? `|lang:${lang}` : '');
  const segActive = subsegs.length > 0 || !!lang;
  const mkBlock = (u, withSeg) => {
    const b = { id: String(u.id), localisation: { zoneType: u.zoneType, zones: [String(u.zone)], include: 1 } };
    // Per Instadata contract: subsegments nest under `segments`; each in its own group →
    // AND within a group, OR between groups (default reverseLogic:false) = "any of these".
    if (withSeg && subsegs.length) {
      b.segments = { subsegments: subsegs.map(c => [{ value: c, include: 1 }]), include: 1 };
    }
    if (withSeg && lang) b.lang = { value: lang, include: 1 }; // verified live 2026-10-27: 8000 fr → 493 dm of 7.114
    return b;
  };
  // `databases` became required on 2026-11 (422 "The databases field is required" → silent GIS
  // fallback). Our key has one partner database, "thewave" (stats.partners in the answer).
  const base = (rep) => ({ types: ['dm', 'em', 'sms', 'audience'], uniqueProfiles: true, sidIndex: 120, databases: ['thewave'], repeatable: rep });
  const rawKey = (u) => `wave|${u.zoneType}|${u.zone}|`;
  const segKey = (u) => `wave|${u.zoneType}|${u.zone}|${sig}`;

  // Elasticsearch caps a query at 1024 nested clauses; a block costs roughly (zones + subsegments)
  // clauses. Raw blocks are cheap (60 per call); segmented chunks shrink with the Dividual count.
  const rawTodo = units.filter(u => !cache.has(rawKey(u)));
  const segTodo = segActive ? units.filter(u => !cache.has(segKey(u))) : [];
  const segChunk = Math.max(4, Math.min(60, Math.floor(800 / (subsegs.length + 3))));
  const jobs = [];
  for (let i = 0; i < rawTodo.length; i += 60) jobs.push({ units: rawTodo.slice(i, i + 60), seg: false });
  for (let i = 0; i < segTodo.length; i += segChunk) jobs.push({ units: segTodo.slice(i, i + segChunk), seg: true });
  const work = Math.max(1, rawTodo.length + segTodo.length);
  let done = 0;
  if (onProgress) onProgress(0, work);

  // A few calls in flight at once: a sector selection is many small calls, not one slow one.
  let next = 0;
  const worker = async () => {
    while (next < jobs.length) {
      const job = jobs[next++];
      // A single slow or failed call used to send the whole count to the GIS fallback: retry twice.
      let resp;
      for (let attempt = 0; ; attempt++) {
        try { resp = await waveCountSegments(base(job.units.map(u => mkBlock(u, job.seg)))); break; }
        catch (e) { if (attempt >= 2) throw e; await new Promise(r => setTimeout(r, 1000 * (attempt + 1))); }
      }
      const rep = resp?.data?.repeatable || {};
      job.units.forEach(u => cache.set(job.seg ? segKey(u) : rawKey(u), rep[String(u.id)] || {}));
      done += job.units.length;
      if (onProgress) onProgress(done, work);
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, jobs.length) }, worker));

  const results = new Map();
  units.forEach(u => {
    const raw = cache.get(rawKey(u)) || {};
    const seg = segActive ? (cache.get(segKey(u)) || {}) : raw; // no segmentation → target = total
    const total = Number(raw.dm || 0);
    const target = Number(seg.dm || 0);
    results.set(u.id, {
      total,
      target,
      pct: total ? (target / total) * 100 : 0,
      emailTotal: Number(raw.em || 0),
      emailTarget: Number(seg.em || 0),
      phoneTotal: Number(raw.sms || 0),
      phoneTarget: Number(seg.sms || 0)
    });
  });
  if (onProgress) onProgress(work, work);

  // Target penetration (the slider's %) comes from the ADDRESS data, not from The Wave's profiles:
  // The Wave is an opt-in sample and skewed per segment (H "welgestelde senioren" runs at about
  // half its real share, E above it — checked against GIS per postcode on 2026-10-15), while GIS
  // counts every address with its Dividual, the same basis Distritool uses. GIS knows postcodes
  // only, so every unit takes its postcode's share; per-sector shares wait for a NIS9 endpoint.
  if (subsegs.length) {
    const postcodes = [...new Set(units.map(u => u.postcode).filter(Boolean))];
    const perPc = new Map();
    await Promise.all(postcodes.map(async (pc) => {
      try { perPc.set(pc, await gisPostcode(pc, subsegs, sig)); }
      catch (e) { console.warn('GIS share failed for', pc, e.message); }
    }));
    units.forEach(u => {
      const g = u.postcode ? perPc.get(u.postcode) : null;
      const r = results.get(u.id);
      if (g && r) { r.pct = g.pct; r.pctSource = 'gis'; }
      else if (r) r.pctSource = 'wave';
    });
  }
  return results;
}
const PROVIDERS = { gis: gisProvider, instadata: instadataProvider };

export let COUNT_PROVIDER = 'instadata'; // real Wave data (households=dm, email=em, phone=sms) via the proxy
export function setCountProvider(name) { if (PROVIDERS[name]) COUNT_PROVIDER = name; }

// When The Wave proxy is unreachable (the static-IP box is down → the Function answers 5xx or
// times out), fall back to GIS so households/target stay real; e-mail/phone are then the mock
// estimates. Remember the failure for a while so every recount doesn't first wait on a dead host.
const FALLBACK_HOLD_MS = 60 * 1000; // retry The Wave after a minute
let waveDownUntil = 0;
let lastSource = null; // 'instadata' | 'gis' | 'gis-fallback'
export function lastCountSource() { return lastSource; }

// units: count units (see above); subsegs: string[] -> Map<unit id, {total,target,pct,emailTotal,…}>
export async function fetchCounts(units, subsegs, onProgress, opts = {}) {
  if (!units.length) return new Map();
  if (COUNT_PROVIDER !== 'instadata') {
    lastSource = COUNT_PROVIDER;
    return PROVIDERS[COUNT_PROVIDER](units, subsegs, onProgress);
  }
  if (Date.now() < waveDownUntil) {
    lastSource = 'gis-fallback';
    return gisProvider(units, subsegs, onProgress);
  }
  try {
    const r = await instadataProvider(units, subsegs, onProgress, opts);
    lastSource = 'instadata';
    return r;
  } catch (e) {
    console.warn('The Wave count failed, falling back to GIS:', e.message);
    waveDownUntil = Date.now() + FALLBACK_HOLD_MS;
    lastSource = 'gis-fallback';
    return gisProvider(units, subsegs, onProgress);
  }
}
